import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchLatestPlan,
  createPlan,
  updatePlan,
  updatePlanSection,
  refinePlanSection,
  rebalancePlan,
  generateAssignments,
  fetchAssignments,
  updateAssignment,
  fetchAnalytics,
  fetchCanvasAssignments,
  fetchCanvasSubmissions,
  postCanvasAssignment,
  createAssignmentArtifacts,
  addAssignmentArtifact,
  fetchPlanTimeline,
  fetchPlanById,
} from "../../utils/lessonPlanApi";

const initialState = {
  status: "idle",
  error: null,
  plans: [],
  timelineCursor: null,
  hasMorePlans: true,
  currentPlanId: null,
  sectionsByPlan: {},
  assignmentsByPlan: {},
  analyticsByPlan: {},
  canvasAssignments: [],
  canvasError: null,
  canvasLastSync: null,
  driveLastSync: null,
  timelineLoading: false,
  timelineError: null,
  sourcePlanLoading: false,
  sourcePlanError: null,
  retryLoadError: null,
};

export const loadLatestPlan = createAsyncThunk(
  "lessonPlan/loadLatestPlan",
  async () => fetchLatestPlan()
);

export const loadPlanTimeline = createAsyncThunk(
  "lessonPlan/loadPlanTimeline",
  async (cursor) => fetchPlanTimeline(cursor)
);

export const loadPlanById = createAsyncThunk(
  "lessonPlan/loadPlanById",
  async (planId) => fetchPlanById(planId)
);

export const createNextPlan = createAsyncThunk(
  "lessonPlan/createNextPlan",
  async (payload) => createPlan(payload)
);

export const savePlanUpdate = createAsyncThunk(
  "lessonPlan/savePlanUpdate",
  async ({ planId, updates }) => updatePlan(planId, updates)
);

export const savePlanSection = createAsyncThunk(
  "lessonPlan/savePlanSection",
  async ({ planId, sectionId, content, provenance, supportingDocuments }) =>
    updatePlanSection(
      planId,
      sectionId,
      content,
      provenance,
      supportingDocuments
    )
);

export const refineSection = createAsyncThunk(
  "lessonPlan/refineSection",
  async ({ planId, sectionId, payload }) =>
    refinePlanSection(planId, sectionId, payload)
);

export const rebalanceEntirePlan = createAsyncThunk(
  "lessonPlan/rebalanceEntirePlan",
  async ({ planId, payload }) => rebalancePlan(planId, payload)
);

export const loadAssignments = createAsyncThunk(
  "lessonPlan/loadAssignments",
  async (planId) => fetchAssignments(planId)
);

export const createAssignments = createAsyncThunk(
  "lessonPlan/createAssignments",
  async ({ planId, payload }) => generateAssignments(planId, payload)
);

export const saveAssignmentUpdate = createAsyncThunk(
  "lessonPlan/saveAssignmentUpdate",
  async ({ assignmentId, updates }) => updateAssignment(assignmentId, updates)
);

export const loadAnalytics = createAsyncThunk(
  "lessonPlan/loadAnalytics",
  async (planId) => fetchAnalytics(planId)
);

export const loadCanvasAssignments = createAsyncThunk(
  "lessonPlan/loadCanvasAssignments",
  async () => fetchCanvasAssignments()
);

export const loadCanvasSubmissions = createAsyncThunk(
  "lessonPlan/loadCanvasSubmissions",
  async () => fetchCanvasSubmissions()
);

export const postAssignmentToCanvas = createAsyncThunk(
  "lessonPlan/postAssignmentToCanvas",
  async (payload) => postCanvasAssignment(payload)
);

export const buildAssignmentArtifacts = createAsyncThunk(
  "lessonPlan/buildAssignmentArtifacts",
  async (payload) => createAssignmentArtifacts(payload)
);

export const attachAssignmentArtifact = createAsyncThunk(
  "lessonPlan/attachAssignmentArtifact",
  async (payload) => addAssignmentArtifact(payload)
);

const lessonPlanSlice = createSlice({
  name: "lessonPlan",
  initialState,
  reducers: {
    setCurrentPlanId(state, action) {
      state.currentPlanId = action.payload;
    },
    clearLessonPlanError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadLatestPlan.pending, (state) => {
        state.status = "loading";
      })
      .addCase(loadLatestPlan.fulfilled, (state, action) => {
        state.status = "succeeded";
        const { plan, sections } = action.payload || {};
        if (plan) {
          state.plans = [plan, ...state.plans.filter((p) => p.id !== plan.id)];
          state.currentPlanId = plan.id;
          state.sectionsByPlan[plan.id] = sections || [];
        }
      })
      .addCase(loadLatestPlan.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error?.message || "Failed to load latest plan.";
      })
      .addCase(loadPlanTimeline.fulfilled, (state, action) => {
        const { plans, cursor, hasMore } = action.payload || {};
        const existingIds = new Set(state.plans.map((plan) => plan.id));
        const nextPlans = (plans || []).filter((plan) => !existingIds.has(plan.id));
        state.plans = state.plans.concat(nextPlans);
        state.timelineCursor = cursor || null;
        state.hasMorePlans = Boolean(hasMore);
        state.timelineLoading = false;
        state.timelineError = null;
        state.retryLoadError = null;
        if (!state.currentPlanId && state.plans.length) {
          state.currentPlanId = state.plans[0].id;
        }
      })
      .addCase(loadPlanTimeline.pending, (state) => {
        state.timelineLoading = true;
      })
      .addCase(loadPlanTimeline.rejected, (state, action) => {
        state.timelineLoading = false;
        state.timelineError =
          action.error?.message || "Unable to load more plans.";
        state.retryLoadError = "Retry failed.";
      })
      .addCase(loadPlanById.fulfilled, (state, action) => {
        const { plan, sections } = action.payload || {};
        if (!plan) {
          state.sourcePlanLoading = false;
          state.sourcePlanError = "Source plan not found.";
          return;
        }
        const exists = state.plans.some((item) => item.id === plan.id);
        if (!exists) {
          state.plans = [plan, ...state.plans];
        }
        state.currentPlanId = plan.id;
        state.sectionsByPlan[plan.id] = sections || [];
        state.sourcePlanLoading = false;
        state.sourcePlanError = null;
      })
      .addCase(loadPlanById.pending, (state) => {
        state.sourcePlanLoading = true;
        state.sourcePlanError = null;
      })
      .addCase(loadPlanById.rejected, (state, action) => {
        state.sourcePlanLoading = false;
        state.sourcePlanError =
          action.error?.message || "Unable to load source plan.";
      })
      .addCase(createNextPlan.fulfilled, (state, action) => {
        const { plan, sections } = action.payload || {};
        if (plan) {
          state.plans = [plan, ...state.plans];
          state.currentPlanId = plan.id;
          state.sectionsByPlan[plan.id] = sections || [];
        }
      })
      .addCase(savePlanUpdate.fulfilled, (state, action) => {
        const updatedPlan = action.payload?.plan;
        if (updatedPlan) {
          state.plans = state.plans.map((plan) =>
            plan.id === updatedPlan.id ? updatedPlan : plan
          );
        }
      })
      .addCase(savePlanSection.fulfilled, (state, action) => {
        const { planId, section } = action.payload || {};
        if (planId && section) {
          const sections = state.sectionsByPlan[planId] || [];
          state.sectionsByPlan[planId] = sections.map((item) =>
            item.id === section.id ? section : item
          );
        }
      })
      .addCase(loadAssignments.fulfilled, (state, action) => {
        const { planId, assignments } = action.payload || {};
        if (planId) {
          state.assignmentsByPlan[planId] = assignments || [];
        }
      })
      .addCase(createAssignments.fulfilled, (state, action) => {
        const { planId, assignments } = action.payload || {};
        if (planId) {
          state.assignmentsByPlan[planId] = assignments || [];
        }
      })
      .addCase(saveAssignmentUpdate.fulfilled, (state, action) => {
        const { planId, assignmentId, assignment } = action.payload || {};
        if (!planId || !assignmentId || !assignment) return;
        const assignments = state.assignmentsByPlan[planId] || [];
        state.assignmentsByPlan[planId] = assignments.map((item) =>
          item.id === assignmentId ? assignment : item
        );
      })
      .addCase(postAssignmentToCanvas.fulfilled, (state, action) => {
        const { planId, assignmentId, assignment } = action.payload || {};
        if (!planId || !assignmentId || !assignment) return;
        const assignments = state.assignmentsByPlan[planId] || [];
        state.assignmentsByPlan[planId] = assignments.map((item) =>
          item.id === assignmentId ? assignment : item
        );
      })
      .addCase(loadAnalytics.fulfilled, (state, action) => {
        const { planId, analytics } = action.payload || {};
        if (planId) {
          state.analyticsByPlan[planId] = analytics || null;
        }
      })
      .addCase(loadCanvasAssignments.fulfilled, (state, action) => {
        state.canvasAssignments = action.payload?.assignments || [];
        state.canvasError = null;
        state.canvasLastSync = Date.now();
      })
      .addCase(loadCanvasAssignments.rejected, (state, action) => {
        state.canvasError =
          action.error?.message || "Canvas assignments unavailable.";
      })
      .addCase(loadCanvasSubmissions.fulfilled, (state, action) => {
        const submissions = action.payload?.submissions || [];
        const warning = action.payload?.warning || null;
        const counts = submissions.reduce((acc, submission) => {
          const assignmentId = submission.assignmentId;
          if (!assignmentId) return acc;
          acc[assignmentId] = acc[assignmentId] || {
            submissionsCount: 0,
            gradedCount: 0,
          };
          acc[assignmentId].submissionsCount += 1;
          if (submission.grade || submission.graded) {
            acc[assignmentId].gradedCount += 1;
          }
          return acc;
        }, {});

        state.canvasAssignments = state.canvasAssignments.map((assignment) => {
          const count = counts[assignment.id];
          if (!count) return assignment;
          return {
            ...assignment,
            submissionsCount: count.submissionsCount,
            gradedCount: count.gradedCount,
          };
        });
        state.canvasError = null;
        state.canvasLastSync = Date.now();
        if (warning) {
          state.canvasError = warning;
        }
      })
      .addCase(loadCanvasSubmissions.rejected, (state, action) => {
        state.canvasError =
          action.error?.message || "Canvas submissions unavailable.";
      })
      .addCase(attachAssignmentArtifact.fulfilled, (state, action) => {
        const { planId, assignmentId, artifacts } = action.payload || {};
        if (!planId) return;
        const assignments = state.assignmentsByPlan[planId] || [];
        state.assignmentsByPlan[planId] = assignments.map((assignment) =>
          assignment.id === assignmentId
            ? { ...assignment, artifacts: artifacts || [] }
            : assignment
        );
      })
      .addCase(buildAssignmentArtifacts.fulfilled, (state, action) => {
        const { assignmentId, artifacts } = action.payload || {};
        if (!assignmentId || !artifacts) return;
        Object.keys(state.assignmentsByPlan).forEach((planId) => {
          state.assignmentsByPlan[planId] = state.assignmentsByPlan[planId].map(
            (assignment) =>
              assignment.id === assignmentId
                ? { ...assignment, artifacts }
                : assignment
          );
        });
        state.driveLastSync = Date.now();
      });
  },
});

export const { setCurrentPlanId, clearLessonPlanError } =
  lessonPlanSlice.actions;

export const lessonPlanReducer = lessonPlanSlice.reducer;
